import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Plus, Trash2, Pencil, Send, Printer, RefreshCw, Loader2, X,
  Users, Calendar, Percent, Mail, Building2, ChevronDown, Save, Download,
  Settings, ShieldCheck, CheckCircle2, AlertCircle, Eye, EyeOff,
} from "lucide-react";
import {
  listVendors, createVendor, updateVendor, deleteVendor,
  listInvoices, createInvoice, updateInvoice, deleteInvoice, sendInvoice, updateInvoiceStatus,
  fetchMenu, fetchInvoicePdfBlob,
  fetchGmailSettings, saveGmailSettings, clearGmailSettings, testGmailSettings,
} from "@/lib/api";

// ----- helpers -----
const p2 = (n) => String(n).padStart(2, "0");
const localISO = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const mondayOf = (isoOrDate) => {
  const d = typeof isoOrDate === "string" ? new Date(isoOrDate + "T00:00:00") : new Date(isoOrDate);
  const day = d.getDay(); // Sun=0..Sat=6
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return localISO(d);
};
const sundayOfMon = (mondayISO) => {
  const d = new Date(mondayISO + "T00:00:00");
  d.setDate(d.getDate() + 6);
  return localISO(d);
};
const currentMonday = () => mondayOf(new Date());
const fmt = (sym, n) => `${sym}${Number(n || 0).toFixed(2)}`;

// Invoice status lifecycle: Pending (draft) → Sent → Paid → Overdue
const INVOICE_STATUS_OPTIONS = [
  { value: "draft", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];
const statusLabel = (s) => (INVOICE_STATUS_OPTIONS.find((o) => o.value === s)?.label) || (s || "Pending");
const statusBadgeClass = (s) => {
  switch (s) {
    case "paid": return "bg-dotr-primary text-white";
    case "sent": return "bg-dotr-ink text-white";
    case "overdue": return "bg-red-600 text-white";
    case "draft":
    default: return "bg-dotr-yellow text-dotr-ink";
  }
};

// ----- Vendor manager (inline drawer) -----
const VendorManager = ({ vendors, onChanged, onClose }) => {
  const [form, setForm] = useState({ id: null, name: "", email: "", address: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const startEdit = (v) => setForm({ id: v.id, name: v.name || "", email: v.email || "", address: v.address || "", phone: v.phone || "" });
  const reset = () => setForm({ id: null, name: "", email: "", address: "", phone: "" });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Vendor name is required");
    setSaving(true);
    try {
      if (form.id) {
        await updateVendor(form.id, { name: form.name, email: form.email || null, address: form.address || null, phone: form.phone || null });
        toast.success("Vendor updated");
      } else {
        await createVendor({ name: form.name, email: form.email || null, address: form.address || null, phone: form.phone || null });
        toast.success("Vendor added");
      }
      reset();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? Existing invoices will keep the snapshot.`)) return;
    try {
      await deleteVendor(v.id);
      toast.success("Vendor removed");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose} data-testid="vendor-manager-modal">
      <div className="brutal-card w-full max-w-3xl bg-dotr-bg my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4 md:p-5">
          <div className="flex items-center gap-2">
            <Users size={16} />
            <span className="font-heading font-semibold text-xl uppercase tracking-tight">Wholesale vendors</span>
          </div>
          <button onClick={onClose} className="brutal-btn-ghost w-10 h-10 flex items-center justify-center" aria-label="Close" data-testid="vendor-manager-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input data-testid="vendor-input-name" className="brutal-input h-11 px-3" placeholder="Vendor name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            <input data-testid="vendor-input-email" className="brutal-input h-11 px-3" placeholder="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
            <input data-testid="vendor-input-address" className="brutal-input h-11 px-3 md:col-span-2" placeholder="Address" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
            <input data-testid="vendor-input-phone" className="brutal-input h-11 px-3" placeholder="Phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} data-testid="vendor-save" className="brutal-btn px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-70">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {form.id ? "Update vendor" : "Add vendor"}
            </button>
            {form.id && <button onClick={reset} className="brutal-btn-ghost px-4 py-2.5 text-sm">Cancel edit</button>}
          </div>

          <div className="border-t-2 border-dotr-ink pt-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-2">Existing vendors ({vendors.length})</div>
            {vendors.length === 0 ? (
              <div className="font-mono text-xs text-dotr-inkmuted py-4">No vendors yet — add one above.</div>
            ) : (
              <div className="divide-y-2 divide-dotr-ink/20">
                {vendors.map((v) => (
                  <div key={v.id} data-testid={`vendor-row-${v.id}`} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-base uppercase tracking-tight truncate">{v.name}</div>
                      <div className="font-mono text-[11px] text-dotr-inkmuted truncate">
                        {[v.email, v.phone, v.address].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => startEdit(v)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-yellow flex items-center justify-center" aria-label="Edit"><Pencil size={13} /></button>
                      <button onClick={() => remove(v)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-primary hover:text-white flex items-center justify-center" aria-label="Delete"><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ----- Invoice create / edit modal -----
const InvoiceEditor = ({ vendors, menu, sym, existing, onClose, onSaved }) => {
  const isEdit = !!existing;
  const [vendorId, setVendorId] = useState(existing?.vendor_id || (vendors[0]?.id ?? ""));
  const [periodStart, setPeriodStart] = useState(existing?.period_start || currentMonday());
  const [vatApplied, setVatApplied] = useState(existing?.vat_applied ?? false);
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);

  // Build a compact { name -> qty } cart. When editing, sum quantities in case
  // the same item appears split across tier-1 and tier-2 rows.
  const seed = useMemo(() => {
    const m = {};
    (existing?.items || []).forEach((it) => { m[it.name] = (m[it.name] || 0) + (it.quantity || 0); });
    return m;
  }, [existing]);
  const [cart, setCart] = useState(seed);

  const monday = useMemo(() => mondayOf(periodStart), [periodStart]);
  const sunday = useMemo(() => sundayOfMon(monday), [monday]);

  // Live preview of discount tiers (mirror of backend _split_discount_lines).
  const preview = useMemo(() => {
    const priceMap = {};
    (menu || []).forEach((c) => c.items.forEach((it) => { priceMap[it.name] = it.price; }));
    let remaining = 100;
    const lines = [];
    Object.entries(cart).forEach(([name, qty]) => {
      let left = parseInt(qty, 10) || 0;
      const menuP = priceMap[name] ?? 0;
      while (left > 0) {
        if (remaining > 0) {
          const take = Math.min(left, remaining);
          const unit = +(menuP - 1.5).toFixed(2);
          lines.push({ name, quantity: take, menu_price: menuP, discount_per_unit: 1.5, unit_price: unit, line_total: +(unit * take).toFixed(2), tier: 1 });
          remaining -= take; left -= take;
        } else {
          const unit = +(menuP - 2).toFixed(2);
          lines.push({ name, quantity: left, menu_price: menuP, discount_per_unit: 2, unit_price: unit, line_total: +(unit * left).toFixed(2), tier: 2 });
          left = 0;
        }
      }
    });
    const total_units = lines.reduce((s, l) => s + l.quantity, 0);
    const subtotal = +lines.reduce((s, l) => s + l.line_total, 0).toFixed(2);
    const vat = vatApplied ? +(subtotal * 0.20).toFixed(2) : 0;
    const total = +(subtotal + vat).toFixed(2);
    const total_menu_value = +lines.reduce((s, l) => s + l.menu_price * l.quantity, 0).toFixed(2);
    return { lines, total_units, subtotal, vat, total, total_menu_value, total_discount: +(total_menu_value - subtotal).toFixed(2) };
  }, [cart, menu, vatApplied]);

  const bump = (name, delta) => setCart((c) => {
    const q = (c[name] || 0) + delta;
    const nc = { ...c };
    if (q <= 0) delete nc[name]; else nc[name] = q;
    return nc;
  });
  const set = (name, val) => setCart((c) => {
    const q = parseInt(val, 10);
    const nc = { ...c };
    if (!Number.isFinite(q) || q <= 0) delete nc[name]; else nc[name] = q;
    return nc;
  });

  const save = async () => {
    if (!vendorId) return toast.error("Pick a vendor");
    if (Object.keys(cart).length === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const items = Object.entries(cart).map(([name, quantity]) => ({ name, quantity: parseInt(quantity, 10) }));
      const payload = { vendor_id: vendorId, period_start: monday, period_end: sunday, items, vat_applied: vatApplied, notes: notes || null };
      const res = isEdit ? await updateInvoice(existing.id, payload) : await createInvoice(payload);
      toast.success(isEdit ? "Invoice updated" : `Invoice ${res.invoice_number} created`);
      onSaved?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose} data-testid="invoice-editor-modal">
      <div className="brutal-card w-full max-w-6xl bg-dotr-bg my-4" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-dotr-bg z-10 flex items-center justify-between border-b-2 border-dotr-ink p-4 md:p-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">Weekly invoice · Mon → Sun</div>
            <div className="font-heading font-semibold text-xl md:text-2xl uppercase tracking-tight">
              {isEdit ? `Edit ${existing.invoice_number}` : "New wholesale invoice"}
            </div>
          </div>
          <button onClick={onClose} className="brutal-btn-ghost w-10 h-10 flex items-center justify-center" aria-label="Close" data-testid="invoice-editor-close"><X size={16} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] gap-0">
          <div className="p-5 md:p-6 space-y-5 lg:border-r-2 lg:border-dotr-ink">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="label-uppercase mb-1.5 block">Vendor *</label>
                <select data-testid="invoice-vendor-select" value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="brutal-input h-11 px-3 w-full bg-white">
                  <option value="">— select —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-uppercase mb-1.5 block">Week of *</label>
                <input data-testid="invoice-week-input" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="brutal-input h-11 px-3 w-full" />
                <span className="font-mono text-[10px] text-dotr-inkmuted mt-1 block">Week: {monday} → {sunday}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label-uppercase !mb-0">Line items</label>
                <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">First 100 units £1.50 off · beyond £2 off</span>
              </div>
              <div className="border-2 border-dotr-ink bg-white max-h-[52vh] overflow-y-auto">
                {(menu || []).map((cat) => (
                  <div key={cat.category} className="border-b border-dotr-ink/30 last:border-b-0">
                    <div className="bg-dotr-muted px-3 py-1.5 font-heading text-[10px] uppercase tracking-[0.14em]">{cat.category}</div>
                    <div>
                      {cat.items.map((it) => {
                        const qty = cart[it.name] || 0;
                        return (
                          <div key={it.name} data-testid={`invoice-item-row-${it.name}`} className="flex items-center gap-2 px-3 py-2 border-b border-dotr-ink/10 last:border-b-0">
                            <div className="flex-1 min-w-0">
                              <div className="font-body text-sm truncate">{it.name}</div>
                              <div className="font-mono text-[10px] text-dotr-inkmuted">{fmt(sym, it.price)} menu</div>
                            </div>
                            <button onClick={() => bump(it.name, -1)} disabled={qty <= 0} className="w-7 h-7 border-2 border-dotr-ink bg-white hover:bg-dotr-muted disabled:opacity-40 flex items-center justify-center" data-testid={`invoice-item-dec-${it.name}`} aria-label={`Decrease ${it.name}`}>−</button>
                            <input
                              type="number" min="0"
                              value={qty || ""}
                              onChange={(e) => set(it.name, e.target.value)}
                              className="w-16 h-7 border-2 border-dotr-ink text-center font-mono text-sm"
                              placeholder="0"
                              data-testid={`invoice-item-qty-${it.name}`}
                            />
                            <button onClick={() => bump(it.name, 1)} className="w-7 h-7 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" data-testid={`invoice-item-inc-${it.name}`} aria-label={`Increase ${it.name}`}>+</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="label-uppercase mb-1.5 block">Notes (optional)</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="brutal-input w-full p-3" data-testid="invoice-notes" />
            </div>
          </div>

          {/* Preview / totals */}
          <div className="p-5 md:p-6 bg-dotr-muted/30 lg:sticky lg:top-[80px] lg:self-start">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-2">Preview</div>
            <div className="brutal-card p-4 mb-4 bg-white">
              <div className="font-mono text-[11px] text-dotr-inkmuted mb-3">
                {preview.total_units} unit{preview.total_units === 1 ? "" : "s"} · {Object.keys(cart).length} item types
              </div>
              <div className="space-y-1.5 max-h-[38vh] overflow-y-auto pr-1">
                {preview.lines.map((l, i) => (
                  <div key={i} className="flex items-center justify-between text-xs font-mono border-b border-dashed border-dotr-ink/20 pb-1.5">
                    <div className="min-w-0 flex-1 truncate">
                      <span>{l.name}</span>
                      <span className="ml-1 text-dotr-inkmuted">×{l.quantity}</span>
                      <span className={`ml-1 text-[9px] px-1 border ${l.tier === 1 ? "border-dotr-ink" : "border-dotr-primary text-dotr-primary"}`}>T{l.tier}</span>
                    </div>
                    <span className="font-bold ml-2">{fmt(sym, l.line_total)}</span>
                  </div>
                ))}
                {preview.lines.length === 0 && <div className="text-xs text-dotr-inkmuted italic">Add items to preview…</div>}
              </div>
            </div>
            <div className="brutal-card p-4 bg-white space-y-2">
              <div className="flex justify-between font-mono text-xs"><span>Menu value</span><span>{fmt(sym, preview.total_menu_value)}</span></div>
              <div className="flex justify-between font-mono text-xs text-dotr-primary"><span>Discount</span><span>−{fmt(sym, preview.total_discount)}</span></div>
              <div className="flex justify-between font-mono text-sm border-t-2 border-dotr-ink pt-2"><span>Subtotal</span><span>{fmt(sym, preview.subtotal)}</span></div>
              <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
                <input type="checkbox" data-testid="invoice-vat-toggle" checked={vatApplied} onChange={(e) => setVatApplied(e.target.checked)} className="w-4 h-4" />
                <span className="flex-1">Apply VAT (20%)</span>
                <span>{fmt(sym, preview.vat)}</span>
              </label>
              <div className="flex justify-between font-heading text-2xl border-t-2 border-dotr-ink pt-2 tracking-tight">
                <span>Total</span>
                <span className="text-dotr-primary" data-testid="invoice-preview-total">{fmt(sym, preview.total)}</span>
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <button onClick={save} disabled={saving} data-testid="invoice-save" className="brutal-btn w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-70">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {isEdit ? "Save changes" : "Create draft invoice"}
              </button>
              <button onClick={onClose} className="brutal-btn-ghost w-full py-2.5 text-xs">Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ----- Send-email modal -----
const SendModal = ({ invoice, onClose, onSent }) => {
  const [to, setTo] = useState(invoice?.vendor_snapshot?.email || "");
  const [subject, setSubject] = useState(`Invoice ${invoice.invoice_number} — Dum on the Run`);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) return toast.error("Recipient email required");
    setSending(true);
    try {
      await sendInvoice(invoice.id, { to_email: to.trim(), subject, body: body || null });
      toast.success(`Sent to ${to}`);
      onSent?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (e?.code === "ECONNABORTED" ? "Timed out while sending — the email may still arrive. Try again in a minute." : null)
        || e?.message
        || "Send failed";
      console.error("Invoice send failed:", e);
      toast.error(detail, { duration: 8000 });
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose} data-testid="invoice-send-modal">
      <div className="brutal-card w-full max-w-lg bg-dotr-bg my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4">
          <div className="flex items-center gap-2"><Mail size={14} /><span className="font-heading font-semibold uppercase">Email {invoice.invoice_number}</span></div>
          <button onClick={onClose} className="brutal-btn-ghost w-9 h-9 flex items-center justify-center" data-testid="invoice-send-close"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="label-uppercase mb-1.5 block">To *</label>
            <input data-testid="invoice-send-to" value={to} onChange={(e) => setTo(e.target.value)} className="brutal-input h-11 px-3 w-full" type="email" />
          </div>
          <div>
            <label className="label-uppercase mb-1.5 block">Subject</label>
            <input data-testid="invoice-send-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="brutal-input h-11 px-3 w-full" />
          </div>
          <div>
            <label className="label-uppercase mb-1.5 block">Message (optional)</label>
            <textarea data-testid="invoice-send-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} className="brutal-input p-3 w-full" placeholder="Default: 'Please find attached invoice …'" />
          </div>
        </div>
        <div className="border-t-2 border-dotr-ink p-4 flex justify-between items-center">
          <span className="font-mono text-[10px] text-dotr-inkmuted uppercase tracking-widest">PDF auto-attached</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="brutal-btn-ghost px-4 py-2 text-xs">Cancel</button>
            <button onClick={send} disabled={sending} data-testid="invoice-send-btn" className="brutal-btn px-5 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-70">
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ----- Gmail settings modal -----
const EmailSettingsModal = ({ initial, onClose, onSaved }) => {
  const [user, setUser] = useState(initial?.user || "");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);

  const isConfigured = !!initial?.configured;
  const fromEnv = initial?.source === "environment";

  // Format of a valid Gmail app password: exactly 16 lowercase ASCII letters.
  const validate = (rawPw) => {
    const cleaned = rawPw.replace(/[\s\u200b\u200c\u200d\u2060\ufeff\u00a0]/g, "");
    if (!cleaned) return { ok: false, msg: "Enter your Gmail app password below." };
    const looksLikeAccountPw = /[A-Z]/.test(cleaned) || /[^a-z0-9]/.test(cleaned) || /\d/.test(cleaned);
    if (looksLikeAccountPw) {
      return {
        ok: false,
        long: true,
        msg: "That looks like your Google account password — Gmail app passwords are 16 LOWERCASE letters, no digits, no symbols. Generate one at https://myaccount.google.com/apppasswords (needs 2-Step Verification on your account).",
      };
    }
    if (cleaned.length !== 16) {
      return { ok: false, msg: `App password must be exactly 16 lowercase letters. Yours is ${cleaned.length}.` };
    }
    return { ok: true, cleaned };
  };

  const save = async () => {
    if (!user.trim() || !user.includes("@")) return toast.error("Enter a valid Gmail address");
    const v = validate(pw);
    if (!v.ok) return toast.error(v.msg, v.long ? { duration: 15000 } : undefined);
    setSaving(true);
    try {
      const res = await saveGmailSettings({ user: user.trim(), app_password: v.cleaned });
      toast.success("Email credentials saved.");
      onSaved?.(res);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    if (!user.trim() || !user.includes("@")) return toast.error("Enter a valid Gmail address first");
    // If the user has typed a new password we validate + save it before testing.
    // If nothing new was typed we test the already-saved credentials as-is.
    if (pw) {
      const v = validate(pw);
      if (!v.ok) return toast.error(v.msg, v.long ? { duration: 15000 } : undefined);
      setTesting(true);
      try {
        await saveGmailSettings({ user: user.trim(), app_password: v.cleaned });
        onSaved?.();
      } catch (e) {
        setTesting(false);
        return toast.error(e?.response?.data?.detail || "Save failed");
      }
    } else if (!isConfigured) {
      return toast.error("Type your Gmail app password first, then click Test.");
    } else {
      setTesting(true);
    }
    try {
      const r = await testGmailSettings();
      if (r?.ok) {
        toast.success(`Gmail connection OK — sending as ${r.user}`);
      } else {
        const parts = [];
        if (r?.error) parts.push(r.error);
        if (r?.hint) parts.push(r.hint);
        toast.error(parts.join(" — ") || "Gmail did not accept these credentials.", { duration: 15000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const clear = async () => {
    if (!window.confirm("Remove saved Gmail credentials? The Send button will fall back to the .env file (if set) or become disabled.")) return;
    setClearing(true);
    try {
      await clearGmailSettings();
      toast.success("Credentials cleared.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally { setClearing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose} data-testid="gmail-settings-modal">
      <div className="brutal-card w-full max-w-lg bg-dotr-bg my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4">
          <div className="flex items-center gap-2">
            <Settings size={16} />
            <span className="font-heading font-semibold uppercase tracking-tight">Email settings</span>
          </div>
          <button onClick={onClose} className="brutal-btn-ghost w-9 h-9 flex items-center justify-center" aria-label="Close" data-testid="gmail-settings-close"><X size={14} /></button>
        </div>

        <div className="p-5 space-y-4">
          {isConfigured && (
            <div
              className={`border-2 border-dotr-ink px-3 py-2 flex items-start gap-2 ${fromEnv ? "bg-dotr-yellow" : "bg-dotr-muted"}`}
              data-testid="gmail-current-state"
            >
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              <div className="text-xs font-mono">
                Currently sending as <b>{initial.user}</b>
                {" · "}password <b>{initial.app_password_masked}</b>
                {initial.app_password_length != null && (
                  <> ({initial.app_password_length} chars {initial.app_password_length === 16 ? "✓" : "⚠ should be 16"})</>
                )}
                {" · "}source: <b>{initial.source}</b>
                {initial.updated_by && (<><br />Last updated by <b>{initial.updated_by}</b> on {String(initial.updated_at || "").slice(0, 10)}.</>)}
                {fromEnv && <><br /><b>Tip:</b> saving here in the UI overrides the .env value.</>}
              </div>
            </div>
          )}

          <div className="border-2 border-dotr-primary bg-dotr-primary/10 px-3 py-2 flex items-start gap-2" data-testid="gmail-app-password-notice">
            <AlertCircle size={13} className="mt-0.5 shrink-0 text-dotr-primary" />
            <div className="text-[11px] font-mono text-dotr-ink leading-snug">
              <b>Not your Google account password.</b> You need an <b>app password</b> — 16 lowercase letters like <code className="bg-white px-1">abcd efgh ijkl mnop</code>, generated at{" "}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-bold"
              >myaccount.google.com/apppasswords</a>
              . Requires 2-Step Verification on the account first.
            </div>
          </div>

          <div>
            <label className="label-uppercase mb-1.5 block">Gmail address</label>
            <input
              type="email"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="you@gmail.com"
              className="brutal-input h-11 px-3 w-full"
              data-testid="gmail-input-user"
              autoComplete="username"
            />
          </div>

          <div>
            <label className="label-uppercase mb-1.5 block flex items-center gap-2">
              <span>App password</span>
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[10px] normal-case tracking-normal text-dotr-inkmuted underline"
              >
                Get one →
              </a>
            </label>
            <div className="flex gap-2">
              <input
                type={show ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={isConfigured ? "(leave blank to keep saved password)" : "abcd efgh ijkl mnop"}
                className="brutal-input h-11 px-3 flex-1 font-mono"
                data-testid="gmail-input-password"
                autoComplete="new-password"
                spellCheck="false"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="w-11 h-11 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center"
                data-testid="gmail-toggle-visibility"
                aria-label={show ? "Hide password" : "Show password"}
              >
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="mt-1.5 font-mono text-[10px] text-dotr-inkmuted leading-snug">
              Spaces from the pasted password are stripped automatically. Stored in the database — never logged.
            </div>
          </div>

          <div className="border-2 border-dotr-ink/30 bg-white px-3 py-2 flex items-start gap-2">
            <AlertCircle size={13} className="mt-0.5 shrink-0 text-dotr-inkmuted" />
            <div className="text-[11px] font-mono text-dotr-inkmuted leading-snug">
              A "Send" click will use this account. Test the connection first to make sure Google accepts it.
            </div>
          </div>
        </div>

        <div className="border-t-2 border-dotr-ink p-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            {isConfigured && !fromEnv && (
              <button
                type="button"
                onClick={clear}
                disabled={clearing || saving || testing}
                data-testid="gmail-clear"
                className="brutal-btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {clearing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Remove
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={test}
              disabled={testing || saving}
              data-testid="gmail-test"
              className="brutal-btn-ghost px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {testing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Test connection
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || testing}
              data-testid="gmail-save"
              className="brutal-btn px-5 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ----- Main Invoices page -----
export const Invoices = () => {
  const [vendors, setVendors] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [menu, setMenu] = useState([]);
  const [sym, setSym] = useState("£");
  const [loading, setLoading] = useState(true);
  const [showVendors, setShowVendors] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [gmail, setGmail] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {id...} = edit
  const [sending, setSending] = useState(null);
  const [filterVendor, setFilterVendor] = useState("");

  const loadGmail = useCallback(async () => {
    try {
      const g = await fetchGmailSettings();
      setGmail(g);
    } catch {
      setGmail(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, inv, m] = await Promise.all([listVendors(), listInvoices(), fetchMenu()]);
      setVendors(v);
      setInvoices(inv);
      setMenu(m.menu || []);
      setSym(m.currency_symbol || "£");
      loadGmail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoices");
    } finally { setLoading(false); }
  }, [loadGmail]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => (filterVendor ? invoices.filter((i) => i.vendor_id === filterVendor) : invoices), [invoices, filterVendor]);

  const openPreview = async (inv, download = false) => {
    try {
      const blob = await fetchInvoicePdfBlob(inv.id);
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url; a.download = `${inv.invoice_number}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } else {
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) { toast.error("Could not open PDF"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await deleteInvoice(inv.id);
      toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const changeStatus = async (inv, next) => {
    if (next === inv.status) return;
    // Optimistic update
    setInvoices((prev) => prev.map((it) => (it.id === inv.id ? { ...it, status: next } : it)));
    try {
      const updated = await updateInvoiceStatus(inv.id, next);
      setInvoices((prev) => prev.map((it) => (it.id === inv.id ? { ...it, ...updated } : it)));
      toast.success(`Marked as ${statusLabel(next)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
      // rollback
      setInvoices((prev) => prev.map((it) => (it.id === inv.id ? { ...it, status: inv.status } : it)));
    }
  };

  return (
    <div data-testid="page-invoices" className="space-y-5">
      <div className="brutal-card p-5 md:p-6">
        <div className="flex items-start md:items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 border-2 border-dotr-ink bg-dotr-ink text-white flex items-center justify-center"><FileText size={20} /></div>
            <div>
              <h2 className="font-heading font-semibold text-2xl md:text-3xl uppercase tracking-tight">Invoices</h2>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">Weekly Mon → Sun · £1.50 off first 100 units · £2 off beyond · VAT optional</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowEmailSettings(true)}
              data-testid="btn-email-settings"
              className={`brutal-btn-ghost px-3 py-2.5 text-xs inline-flex items-center gap-1.5 ${
                gmail?.configured ? "" : "!bg-dotr-yellow"
              }`}
              title={gmail?.configured ? `Sending as ${gmail.user}` : "Configure email sender"}
            >
              <Settings size={12} />
              <span className="hidden sm:inline">Email</span>
              {gmail?.configured ? (
                <span className="w-2 h-2 rounded-full bg-dotr-primary" data-testid="gmail-status-dot" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-dotr-ink/40" data-testid="gmail-status-dot" />
              )}
            </button>
            <button onClick={() => setShowVendors(true)} data-testid="btn-manage-vendors" className="brutal-btn-ghost px-4 py-2.5 text-xs inline-flex items-center gap-1.5">
              <Building2 size={12} /> Vendors ({vendors.length})
            </button>
            <button
              onClick={() => vendors.length === 0 ? (toast.error("Add a vendor first"), setShowVendors(true)) : setEditing({})}
              data-testid="btn-new-invoice"
              className="brutal-btn px-5 py-2.5 text-sm inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> New invoice
            </button>
            <button onClick={load} disabled={loading} data-testid="btn-refresh-invoices" className="brutal-btn-ghost px-3 py-2.5 text-xs inline-flex items-center gap-1.5">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            </button>
          </div>
        </div>

        {vendors.length > 0 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">Filter by vendor:</span>
            <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)} data-testid="filter-vendor" className="brutal-input h-9 px-3 bg-white text-sm">
              <option value="">All vendors</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading && !invoices.length ? (
        <div className="brutal-card p-16 flex items-center justify-center"><Loader2 size={24} className="animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="brutal-card p-16 text-center" data-testid="invoices-empty">
          <FileText size={30} className="mx-auto mb-3 text-dotr-inkmuted" />
          <div className="font-heading text-xl uppercase tracking-tight mb-1">No invoices yet</div>
          <div className="font-mono text-xs uppercase tracking-widest text-dotr-inkmuted">
            {vendors.length === 0 ? "Add a wholesale vendor to get started" : 'Click "New invoice" to raise one'}
          </div>
        </div>
      ) : (
        <div className="brutal-card overflow-hidden" data-testid="invoices-list">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[880px]">
              <thead>
                <tr className="bg-dotr-ink text-white">
                  {["Number", "Vendor", "Week", "Units", "Subtotal", "VAT", "Total", "Status", ""].map((h) => (
                    <th key={h} className="text-left p-3 font-heading text-[10px] uppercase tracking-[0.14em] border-r border-white/20 last:border-r-0">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b-2 border-dotr-ink/20 hover:bg-dotr-muted/40">
                    <td className="p-3 font-mono text-sm font-bold">{inv.invoice_number}</td>
                    <td className="p-3 font-body">
                      <div className="font-semibold">{inv.vendor_snapshot?.name}</div>
                      <div className="font-mono text-[10px] text-dotr-inkmuted">{inv.vendor_snapshot?.email || "—"}</div>
                    </td>
                    <td className="p-3 font-mono text-xs">
                      <div className="flex items-center gap-1"><Calendar size={11} />{inv.period_start} → {inv.period_end}</div>
                    </td>
                    <td className="p-3 font-mono text-xs">{inv.total_units}</td>
                    <td className="p-3 font-mono text-xs">{fmt(sym, inv.subtotal)}</td>
                    <td className="p-3 font-mono text-xs">
                      {inv.vat_applied ? <span className="inline-flex items-center gap-0.5"><Percent size={10} />{fmt(sym, inv.vat_amount)}</span> : <span className="text-dotr-inkmuted">—</span>}
                    </td>
                    <td className="p-3 font-heading text-sm font-bold text-dotr-primary">{fmt(sym, inv.total)}</td>
                    <td className="p-3">
                      <div className="relative inline-block">
                        <select
                          value={inv.status || "draft"}
                          onChange={(e) => changeStatus(inv, e.target.value)}
                          data-testid={`invoice-status-select-${inv.id}`}
                          title={inv.paid_at ? `Paid at ${new Date(inv.paid_at).toLocaleString()}` : undefined}
                          className={`appearance-none pl-2 pr-6 py-1 border-2 border-dotr-ink font-mono text-[10px] uppercase tracking-widest cursor-pointer focus:outline-none ${statusBadgeClass(inv.status)}`}
                        >
                          {INVOICE_STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value} className="bg-white text-dotr-ink">
                              {o.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={11} className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-80" />
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="inline-flex gap-1">
                        <button onClick={() => openPreview(inv)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" title="Preview / Print PDF" data-testid={`invoice-view-${inv.id}`} aria-label="Preview"><Printer size={13} /></button>
                        <button onClick={() => openPreview(inv, true)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-muted flex items-center justify-center" title="Download PDF" data-testid={`invoice-dl-${inv.id}`} aria-label="Download"><Download size={13} /></button>
                        <button onClick={() => setSending(inv)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-ink hover:text-white flex items-center justify-center" title="Email invoice" data-testid={`invoice-send-${inv.id}`} aria-label="Send"><Send size={13} /></button>
                        <button onClick={() => setEditing(inv)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-yellow flex items-center justify-center" title="Edit" data-testid={`invoice-edit-${inv.id}`} aria-label="Edit"><Pencil size={13} /></button>
                        <button onClick={() => remove(inv)} className="w-9 h-9 border-2 border-dotr-ink bg-white hover:bg-dotr-primary hover:text-white flex items-center justify-center" title="Delete" data-testid={`invoice-del-${inv.id}`} aria-label="Delete"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showVendors && <VendorManager vendors={vendors} onChanged={load} onClose={() => setShowVendors(false)} />}
      {showEmailSettings && (
        <EmailSettingsModal
          initial={gmail}
          onClose={() => setShowEmailSettings(false)}
          onSaved={loadGmail}
        />
      )}
      {editing !== null && (
        <InvoiceEditor
          vendors={vendors}
          menu={menu}
          sym={sym}
          existing={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
      {sending && <SendModal invoice={sending} onClose={() => setSending(null)} onSent={load} />}
    </div>
  );
};

export default Invoices;
